import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Info,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/custom/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  MultiStepDialogContent,
  DialogPage,
} from "@/components/custom/paginated-dialog-content/multi-step-dialog-content";
import { client } from "@/client";
import type { ClusterUploadResponseDto } from "@/client";

import { showActionErrorToast } from "@/state/error-toast";

const uploadClustersSchema = z.object({
  file: z.custom<File>((v) => v instanceof File, {
    message: "File is required",
  }),
});

interface ValidationResults {
  isCsv: boolean;
  recordCount: number;
  foundFields: string[];
  missingFields: string[];
  emptyClusterRows: number[];
  duplicateClusters: string[];
  invalidArchitectureRows: { row: number; value: string }[];
  missingServerRows: { row: number; name: string; architecture: string }[];
  unnecessaryServerRows: { row: number; name: string }[];
  duplicateServers: { server: string; clusters: string[] }[];
  extraFields: string[];
}

const VALID_ARCHITECTURES = ["distributed", "hybrid", "centralized"];

export const UploadClustersDialog = ({
  onClose,
}: {
  onClose?: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const queryClient = useQueryClient();
  const [dryRunResult, setDryRunResult] =
    useState<ClusterUploadResponseDto | null>(null);

  const form = useForm<z.infer<typeof uploadClustersSchema>>({
    resolver: zodResolver(uploadClustersSchema),
    defaultValues: {
      file: undefined,
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (data: { file: File; dryRun: boolean }) =>
      client.clusters.uploadCSV({
        formData: { file: data.file, dryRun: data.dryRun },
      }),
    onSuccess: (data, variables) => {
      console.log(
        "Upload Response (dryRun=" + variables.dryRun + "):",
        JSON.stringify(data),
      );
      if (variables.dryRun) {
        setDryRunResult(data);
        setCurrentStep(3); // Move to Server Verification Step (Page 4)
      } else {
        // Final upload success
        setDryRunResult(data); // Store data for result page
        toast.success(t("Clusters uploaded successfully"));
        queryClient.invalidateQueries({
          queryKey: client.clusters.listSummaries.queryKey,
        });
        setCurrentStep(4); // Move to Results Step (Page 5)
      }
    },
    onError: (error: any) => {
      showActionErrorToast(error, t("Upload failed"));
    },
  });

  const file = form.watch("file");

  // Client-Side Validation State
  const [validationState, setValidationState] = useState<
    "idle" | "validating" | "valid" | "invalid"
  >("idle");
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validationResults, setValidationResults] = useState<ValidationResults>(
    {
      isCsv: false,
      recordCount: 0,
      foundFields: [],
      missingFields: [],
      emptyClusterRows: [],
      duplicateClusters: [],
      invalidArchitectureRows: [],
      missingServerRows: [],
      unnecessaryServerRows: [],
      duplicateServers: [],
      extraFields: [],
    },
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        form.setValue("file", acceptedFiles[0], { shouldValidate: true });
        // Reset validation when file changes
        setValidationState("idle");
        setValidationErrors([]);
        setValidationResults({
          isCsv: false,
          recordCount: 0,
          foundFields: [],
          missingFields: [],
          emptyClusterRows: [],
          duplicateClusters: [],
          invalidArchitectureRows: [],
          missingServerRows: [],
          unnecessaryServerRows: [],
          duplicateServers: [],
          extraFields: [],
        });
        setDryRunResult(null);
      }
    },
    [form],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
    },
    maxFiles: 1,
  });

  const validateFile = async (fileToValidate: File) => {
    setValidationState("validating");
    setValidationErrors([]);
    setValidationResults({
      isCsv: false,
      recordCount: 0,
      foundFields: [],
      missingFields: [],
      emptyClusterRows: [],
      duplicateClusters: [],
      invalidArchitectureRows: [],
      missingServerRows: [],
      unnecessaryServerRows: [],
      duplicateServers: [],
      extraFields: [],
    });

    // Basic file type check
    const isCsv =
      fileToValidate.type === "text/csv" ||
      fileToValidate.name.toLowerCase().endsWith(".csv");

    if (!isCsv) {
      setValidationErrors([t("File must be a CSV file.")]);
      setValidationState("invalid");
      return false;
    }

    try {
      const text = await fileToValidate.text();
      const lines = text.split(/\r\n|\n/);
      if (
        lines.length === 0 ||
        (lines.length === 1 && lines[0].trim() === "")
      ) {
        setValidationErrors([t("File is empty.")]);
        setValidationState("invalid");
        return false;
      }

      const headerLine = lines[0];
      const headers = headerLine.split(",").map((h) => h.trim());

      const errors: string[] = [];
      const foundFields: string[] = [];
      const missingFields: string[] = [];

      // Check required field: cluster
      const clusterIndex = headers.indexOf("cluster");
      const hasCluster = clusterIndex !== -1;
      if (!hasCluster) {
        errors.push(t('Missing required column: "cluster"'));
      } else {
        foundFields.push("cluster");
      }

      // Check optional fields
      const optionalFields = ["architecture", "server", "tags"];
      const knownFields = ["cluster", ...optionalFields];
      const extraFields: string[] = [];
      const archIndex = headers.indexOf("architecture");
      const serverIndex = headers.indexOf("server");

      optionalFields.forEach((field) => {
        if (headers.indexOf(field) !== -1) {
          foundFields.push(field);
        } else {
          missingFields.push(field);
        }
      });

      headers.forEach((header) => {
        if (!knownFields.includes(header)) {
          extraFields.push(header);
        }
      });

      // Row-by-row validation
      const clusterValues = new Set<string>();
      const duplicateClusters = new Set<string>();
      const emptyClusterRows: number[] = [];
      const invalidArchitectureRows: { row: number; value: string }[] = [];
      const missingServerRows: {
        row: number;
        name: string;
        architecture: string;
      }[] = [];
      const unnecessaryServerRows: { row: number; name: string }[] = [];
      const serverToCluster = new Map<string, string[]>();
      let recordCount = 0;

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        recordCount++;
        const row = line.split(",").map((v) => v.trim());

        // 1. Cluster name must be unique and non-empty
        if (clusterIndex !== -1) {
          const name = row[clusterIndex];
          if (!name) {
            emptyClusterRows.push(i + 1);
          } else if (clusterValues.has(name.toLowerCase())) {
            duplicateClusters.add(name);
          } else {
            clusterValues.add(name.toLowerCase());
          }
        }

        // 2. Architecture validation
        if (archIndex !== -1 && row[archIndex]) {
          const archValue = row[archIndex].toLowerCase();
          if (!VALID_ARCHITECTURES.includes(archValue)) {
            invalidArchitectureRows.push({ row: i + 1, value: row[archIndex] });
          }
        }

        // Determine effective architecture (default is "distributed")
        const effectiveArch =
          archIndex !== -1 && row[archIndex]
            ? row[archIndex].toLowerCase()
            : "distributed";
        const hasServer =
          serverIndex !== -1 && row[serverIndex] && row[serverIndex].length > 0;
        const clusterName =
          clusterIndex !== -1 && row[clusterIndex]
            ? row[clusterIndex]
            : `Row ${i + 1}`;

        // 3. Hybrid/centralized must have a server device name
        if (
          (effectiveArch === "hybrid" || effectiveArch === "centralized") &&
          !hasServer
        ) {
          missingServerRows.push({
            row: i + 1,
            name: clusterName,
            architecture: effectiveArch,
          });
        }

        // 4. Distributed should NOT have a server device name
        if (effectiveArch === "distributed" && hasServer) {
          unnecessaryServerRows.push({ row: i + 1, name: clusterName });
        }

        // 5. Track server device uniqueness
        if (hasServer) {
          const serverName = row[serverIndex].toLowerCase();
          if (!serverToCluster.has(serverName)) {
            serverToCluster.set(serverName, []);
          }
          serverToCluster.get(serverName)!.push(clusterName);
        }
      }

      // Check duplicate servers across clusters
      const duplicateServers: { server: string; clusters: string[] }[] = [];
      serverToCluster.forEach((clusters, server) => {
        if (clusters.length > 1) {
          duplicateServers.push({ server, clusters });
        }
      });

      // Add row-level errors to the errors list
      if (emptyClusterRows.length > 0) {
        errors.push(
          t('Empty "cluster" found in rows: {{rows}}', {
            rows:
              emptyClusterRows.slice(0, 5).join(", ") +
              (emptyClusterRows.length > 5
                ? t(" ...and {{count}} more", {
                    count: emptyClusterRows.length - 5,
                  })
                : ""),
          }),
        );
      }

      if (invalidArchitectureRows.length > 0) {
        invalidArchitectureRows.slice(0, 3).forEach(({ row, value }) => {
          errors.push(
            t(
              'Row {{row}}: Invalid architecture "{{value}}". Must be "distributed", "hybrid", or "centralized".',
              { row, value },
            ),
          );
        });
      }

      if (missingServerRows.length > 0) {
        missingServerRows.slice(0, 3).forEach(({ row, name, architecture }) => {
          errors.push(
            t(
              'Row {{row}}: Cluster "{{name}}" with "{{architecture}}" architecture must have a server device.',
              { row, name, architecture },
            ),
          );
        });
      }

      if (unnecessaryServerRows.length > 0) {
        unnecessaryServerRows.slice(0, 3).forEach(({ row, name }) => {
          errors.push(
            t(
              'Row {{row}}: Cluster "{{name}}" with "distributed" architecture should not have a server device.',
              { row, name },
            ),
          );
        });
      }

      if (duplicateServers.length > 0) {
        duplicateServers.forEach(({ server, clusters }) => {
          errors.push(
            t(
              'Server device "{{server}}" is assigned to multiple clusters: {{clusters}}',
              { server, clusters: clusters.join(", ") },
            ),
          );
        });
      }

      setValidationResults({
        isCsv: true,
        recordCount,
        foundFields,
        missingFields,
        emptyClusterRows,
        duplicateClusters: Array.from(duplicateClusters),
        invalidArchitectureRows,
        missingServerRows,
        unnecessaryServerRows,
        duplicateServers,
        extraFields,
      });

      if (errors.length > 0) {
        setValidationErrors(errors);
        setValidationState("invalid");
        return true;
      }

      setValidationState("valid");
      return true;
    } catch (error) {
      console.error("Error reading file:", error);
      setValidationErrors([t("Error reading file.")]);
      setValidationState("invalid");
      return false;
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = "cluster,architecture,server,tags";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "clusters_template.csv";

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = (data: z.infer<typeof uploadClustersSchema>) => {
    // Final upload
    uploadMutation.mutate({ file: data.file, dryRun: false });
  };

  // --- Pages ---

  // Page 1: Preparation
  const Page1 = useMemo(
    () => (
      <div className="space-y-4 pt-2">
        <div className="text-left space-y-4">
          <h3 className="text-lg font-medium">
            {t("How to prepare your file")}
          </h3>

          {/* Important Notes */}
          <div>
            <h4 className="font-medium mb-2 text-sm">
              {t("Important Notes:")}
            </h4>
            <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
              <li>
                {t(
                  "Column 'cluster' must be unique — duplicate names will be rejected.",
                )}
              </li>
              <li>
                {t(
                  'If no architecture is specified, it defaults to "distributed".',
                )}
              </li>
              <li>
                {t(
                  '"hybrid" and "centralized" architectures must include a server device name.',
                )}
              </li>
              <li>
                {t('"distributed" architecture does not use a server device.')}
              </li>
              <li>
                {t(
                  "Each server device can only be assigned to one cluster at a time.",
                )}
              </li>
              <li>
                {t('Tags are merged with existing tags (separated by ";").')}
              </li>
              <li>{t("Other fields will overwrite existing values.")}</li>
            </ul>
          </div>

          {/* Server Device Reassignment Notice */}
          <div className="rounded-md bg-amber-50 p-3 border border-amber-200">
            <div className="flex gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-800">
                <p className="font-medium mb-1">
                  {t("Server Device Reassignment:")}
                </p>
                <p>
                  {t(
                    "If a server device is currently assigned to another cluster, you must also reassign that cluster's server in the same CSV (or set it to distributed). Otherwise, the upload will fail with a fatal error.",
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Required Fields */}
          <div>
            <h4 className="font-medium mb-2 text-sm">
              {t("Required Fields:")}
            </h4>
            <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
              <li>{t("cluster - Cluster name (must be unique)")}</li>
            </ul>
          </div>

          {/* Optional Fields */}
          <div>
            <h4 className="font-medium mb-2 text-sm">
              {t("Optional Fields:")}
            </h4>
            <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
              <li>
                {t(
                  "architecture - distributed (default) / hybrid / centralized",
                )}
              </li>
              <li>{t("server - Server device name")}</li>
              <li>{t("tags - Semicolon-separated list")}</li>
            </ul>
          </div>

          {/* Download Template */}
          <div className="pt-2 space-y-2">
            <p className="text-sm text-muted-foreground text-center">
              {t("Need formatting help? Download our template CSV file")}
            </p>
            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={handleDownloadTemplate}
              >
                <Download className="h-4 w-4" />
                {t("Download Template")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    ),
    [t],
  );

  // Page 2: Select File
  const Page2 = useMemo(
    () => (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">{t("Select File")}</h3>
        <FormField
          control={form.control}
          name="file"
          render={() => (
            <FormItem>
              <FormControl>
                <div
                  {...getRootProps()}
                  className={cn(
                    "border-2 border-dashed rounded-lg p-10 text-center cursor-pointer hover:bg-accent/50 transition-colors flex flex-col items-center justify-center gap-4 min-h-[200px]",
                    isDragActive
                      ? "border-azure bg-azure/5"
                      : "border-muted-foreground/25",
                  )}
                >
                  <input {...getInputProps()} />
                  <div className="rounded-full bg-muted p-4">
                    <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    {file ? (
                      <>
                        <p className="text-sm font-medium">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / 1024).toFixed(2)} KB
                        </p>
                        <Button
                          variant="secondary"
                          size="sm"
                          type="button"
                          className="mt-2"
                        >
                          {t("changeFile")}
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium">
                          {t("Drag and drop your file here")}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t("or click to browse")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {t("Only CSV files are allowed")}
                        </p>
                        <Button
                          variant="secondary"
                          size="sm"
                          type="button"
                          className="mt-2"
                        >
                          {t("Browse")}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    ),
    [file, getRootProps, getInputProps, isDragActive, t],
  );

  // Page 3: Client Validation
  const Page3 = useMemo(
    () => (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">{t("Validation Results")}</h3>

        {validationState === "validating" && (
          <div className="flex flex-col items-center justify-center py-8 gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-azure" />
            <p className="text-sm text-muted-foreground">
              {t("Validating file...")}
            </p>
          </div>
        )}

        {(validationState === "valid" || validationState === "invalid") && (
          <>
            {/* Errors */}
            {validationErrors.length > 0 && (
              <div className="rounded-md bg-red-50 p-4 border border-red-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle
                      className="h-5 w-5 text-red-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {t("Validation Failed")}
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <ul className="list-disc pl-5 space-y-1">
                        {validationErrors.slice(0, 6).map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                      {validationErrors.length > 6 && (
                        <p className="mt-2 font-medium italic">
                          {t("And {{count}} more errors...", {
                            count: validationErrors.length - 6,
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Success info */}
            {validationResults.isCsv && (
              <div className="rounded-md bg-green-50 p-4 border border-green-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <CheckCircle2
                      className="h-5 w-5 text-green-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">
                      {validationState === "valid"
                        ? t("Validation Successful")
                        : t("File Structure")}
                    </h3>
                    <div className="mt-2 text-sm text-green-700">
                      <ul className="list-disc pl-5 space-y-1">
                        <li>{t("File format is correct (CSV)")}</li>
                        {validationResults.foundFields.includes("cluster") && (
                          <li>{t('Found column: "cluster"')}</li>
                        )}
                        <li>
                          {t('Found "{{count}}" records', {
                            count: validationResults.recordCount,
                          })}
                        </li>
                        {validationResults.foundFields
                          .filter((f) => f !== "cluster")
                          .map((field) => (
                            <li key={field}>
                              {t('Found column: "{{field}}"', { field })}
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Warnings */}
            {(validationResults.missingFields.length > 0 ||
              validationResults.duplicateClusters.length > 0 ||
              validationResults.unnecessaryServerRows.length > 0) && (
              <div className="rounded-md bg-amber-50 p-4 border border-amber-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertTriangle
                      className="h-5 w-5 text-amber-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-amber-800">
                      {t("Warnings")}
                    </h3>
                    <div className="mt-2 text-sm text-amber-700">
                      {validationResults.extraFields.length > 0 && (
                        <div className="mb-4">
                          <p className="mb-2 font-medium text-red-600">
                            {t("Unknown Fields Found:")}
                          </p>
                          <ul className="list-disc pl-5 space-y-1 text-red-700">
                            {validationResults.extraFields.map((field) => (
                              <li key={field} className="font-mono text-xs">
                                {field}
                              </li>
                            ))}
                          </ul>
                          <p className="mt-1 text-xs text-red-600/80">
                            {t("These fields will be ignored.")}
                          </p>
                        </div>
                      )}

                      {validationResults.missingFields.length > 0 && (
                        <div className="mb-3 text-xs font-normal">
                          <p className="mb-1 font-medium">
                            {t("Missing optional fields:")}
                          </p>
                          <ul className="list-disc pl-5 space-y-1">
                            {validationResults.missingFields.map((field) => (
                              <li key={field}>
                                {t('"{{field}}" field is missing', { field })}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {validationResults.duplicateClusters.length > 0 && (
                        <div className="mb-3 text-xs font-normal">
                          <p className="mb-1 font-medium">
                            {t("Duplicate cluster names found:")}
                          </p>
                          <ul className="list-disc pl-5 space-y-1">
                            {validationResults.duplicateClusters
                              .slice(0, 5)
                              .map((name) => (
                                <li key={name}>{name}</li>
                              ))}
                            {validationResults.duplicateClusters.length > 5 && (
                              <li className="italic">
                                {t("...and {{count}} more", {
                                  count:
                                    validationResults.duplicateClusters.length -
                                    5,
                                })}
                              </li>
                            )}
                          </ul>
                        </div>
                      )}

                      {validationResults.unnecessaryServerRows.length > 0 && (
                        <div className="text-xs font-normal">
                          <p className="mb-1 font-medium">
                            {t(
                              "Distributed clusters with server device (will be ignored):",
                            )}
                          </p>
                          <ul className="list-disc pl-5 space-y-1">
                            {validationResults.unnecessaryServerRows
                              .slice(0, 5)
                              .map(({ row, name }) => (
                                <li key={row}>
                                  {t('Row {{row}}: "{{name}}"', { row, name })}
                                </li>
                              ))}
                            {validationResults.unnecessaryServerRows.length >
                              5 && (
                              <li className="italic">
                                {t("...and {{count}} more", {
                                  count:
                                    validationResults.unnecessaryServerRows
                                      .length - 5,
                                })}
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Info about defaults */}
            {validationState === "valid" &&
              validationResults.missingFields.includes("architecture") && (
                <div className="rounded-md bg-blue-50 p-3 border border-blue-200">
                  <div className="flex gap-2">
                    <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-800">
                      {t(
                        'The "architecture" column is missing. All clusters will default to "distributed".',
                      )}
                    </p>
                  </div>
                </div>
              )}
          </>
        )}
      </div>
    ),
    [validationState, validationErrors, validationResults, t],
  );

  // Helper to render result items
  const renderResultItems = (result: ClusterUploadResponseDto) => (
    <div className="max-h-[300px] overflow-y-auto border rounded-md">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t("Name")}
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t("Status")}
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t("Details")}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {result.results.map((item, idx) => (
            <tr key={idx}>
              <td
                className={cn(
                  "px-3 py-2 whitespace-nowrap text-sm font-medium",
                  item.status === "new" ? "text-green-600" : "text-black",
                )}
              >
                {item.name}
              </td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                {item.status === "new" ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    {t("New")}
                  </span>
                ) : item.status === "existing" ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                    {t("Update")}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                    {t("Error")}
                  </span>
                )}
              </td>
              <td className="px-3 py-2 text-sm text-gray-500">
                {item.error ? (
                  <span className="text-red-500">{item.error}</span>
                ) : (
                  <div className="flex flex-col gap-0.5 text-xs">
                    {item.architecture && <span>{item.architecture}</span>}
                    {item.server && (
                      <span className="text-gray-500">
                        Server: {item.server}
                      </span>
                    )}
                    {item.tags && item.tags.length > 0 && (
                      <span className="text-gray-400">
                        [{item.tags.join(", ")}]
                      </span>
                    )}
                    {item.warnings && item.warnings.length > 0 && (
                      <span className="text-amber-500">
                        {item.warnings.join("; ")}
                      </span>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Page 4: Server Verification (Dry Run Results)
  const Page4 = useMemo(
    () => (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">
          {t("Server Verification Results")}
        </h3>
        {uploadMutation.isPending && (
          <div className="flex flex-col items-center justify-center py-8 gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-azure" />
            <p className="text-sm text-muted-foreground">
              {t("Verifying with server...")}
            </p>
          </div>
        )}
        {!uploadMutation.isPending && dryRunResult && (
          <div className="space-y-4">
            {dryRunResult.fatalError && (
              <div className="rounded-md bg-red-50 p-4 border border-red-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle
                      className="h-5 w-5 text-red-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {t("Fatal Error")}
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{dryRunResult.fatalError}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-50 p-3 rounded-md text-center">
                <div className="text-xl font-bold text-green-600">
                  {dryRunResult.totalNew}
                </div>
                <div className="text-xs text-green-800">
                  {t("New Clusters")}
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-md text-center">
                <div className="text-xl font-bold text-gray-600">
                  {dryRunResult.totalExisting}
                </div>
                <div className="text-xs text-gray-800">
                  {t("To Be Updated")}
                </div>
              </div>
              <div className="bg-red-50 p-3 rounded-md text-center">
                <div className="text-xl font-bold text-red-600">
                  {dryRunResult.totalErrors}
                </div>
                <div className="text-xs text-red-800">{t("Errors")}</div>
              </div>
            </div>

            {renderResultItems(dryRunResult)}

            {dryRunResult.totalErrors > 0 && (
              <div className="rounded-md bg-red-50 p-4 border border-red-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle
                      className="h-5 w-5 text-red-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {t("Validation Errors")}
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>
                        {t(
                          "Please fix the errors in your CSV file before uploading.",
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    ),
    [uploadMutation.isPending, dryRunResult, t],
  );

  // Page 5: Final Results
  const Page5 = useMemo(
    () => (
      <div className="space-y-4">
        <div className="text-center space-y-2">
          <div className="inline-flex rounded-full bg-green-100 p-3">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-green-800">
            {t("Upload Successful!")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("Clusters have been processed successfully.")}
          </p>
        </div>

        {dryRunResult && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-50 p-3 rounded-md text-center">
                <div className="text-xl font-bold text-green-600">
                  {dryRunResult.totalNew}
                </div>
                <div className="text-xs text-green-800">{t("Created")}</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-md text-center">
                <div className="text-xl font-bold text-gray-600">
                  {dryRunResult.totalExisting}
                </div>
                <div className="text-xs text-gray-800">{t("Updated")}</div>
              </div>
              <div className="bg-red-50 p-3 rounded-md text-center">
                <div className="text-xl font-bold text-red-600">
                  {dryRunResult.totalErrors}
                </div>
                <div className="text-xs text-red-800">{t("Failed")}</div>
              </div>
            </div>

            {renderResultItems(dryRunResult)}
          </div>
        )}
      </div>
    ),
    [t, dryRunResult],
  );

  const pages: DialogPage[] = useMemo(
    () => [
      {
        title: t("Preparation"),
        content: Page1,
      },
      {
        title: t("Select File"),
        content: Page2,
        disableNext: !file,
        blocker: async () => {
          const isValid = await form.trigger("file");
          if (!isValid || !file) return false;
          // Run validation when moving from Select File to Validation Results
          return await validateFile(file);
        },
      },
      {
        title: t("Validation"),
        content: Page3,
        blocker: async () => {
          if (validationState !== "valid") {
            return false;
          }

          // If valid, trigger Server Dry Run
          if (file) {
            uploadMutation.mutate({ file, dryRun: true });
            return false;
          }
          return false;
        },
        hideNext: validationState === "invalid",
      },
      {
        title: t("Server Verification"),
        content: Page4,
        blocker: () => {
          if (uploadMutation.isPending) return false;
          if (dryRunResult && dryRunResult.fatalError) return false;
          if (dryRunResult && dryRunResult.totalErrors > 0) return false;

          // Trigger final upload
          if (file) {
            uploadMutation.mutate({ file, dryRun: false });
            return false; // Wait for onSuccess to advance
          }
          return false;
        },
        disableNext:
          uploadMutation.isPending ||
          (dryRunResult
            ? dryRunResult.totalErrors > 0 || !!dryRunResult.fatalError
            : false),
      },
      {
        title: t("Results"),
        content: Page5,
        hideNext: true,
        hideBack: true,
        footer: (
          <Button variant="outline" onClick={onClose}>
            {t("Done")}
          </Button>
        ),
      },
    ],
    [
      Page1,
      Page2,
      Page3,
      Page4,
      Page5,
      t,
      file,
      validationState,
      uploadMutation,
      onClose,
    ],
  );

  const handleNext = useCallback(async () => {
    setCurrentStep((prev) => prev + 1);
  }, []);

  const handleBack = useCallback(() => {
    if (currentStep === 4) {
      onClose?.();
    } else {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep, onClose]);

  return (
    <div className="w-[600px] min-h-[500px] flex flex-col">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="flex-1 flex flex-col h-full"
        >
          <MultiStepDialogContent
            className="flex-1"
            title={t("Upload Clusters CSV")}
            pages={pages}
            currentStep={currentStep}
            onNext={handleNext}
            onBack={handleBack}
            onSubmit={() => handleSubmit(form.getValues())}
            finishButtonText={t("Upload")}
          />
        </form>
      </Form>
    </div>
  );
};
